/**
 * 런타임 설정. 주소·경로는 여기서만 읽는다 (하드코딩 금지 · CLAUDE.md 프론트 규칙 6).
 */

// ⚠ 타입을 명시한다 — 다른 패키지에서 볼 때 `import.meta.env` 증강이 안 보여 `any` 로 새어 나간다
const rawBase: string | undefined = import.meta.env.VITE_API_BASE_URL?.trim()

/** REST 기본 경로. 비워두면 dev 서버 프록시(/api)를 탄다. 계약 base = /api/v1 */
export const API_BASE_URL: string = rawBase ? rawBase.replace(/\/+$/, '') : '/api/v1'

/** 구독 채널 2개. events.asyncapi.yaml 기준 — SSE 없음, WebSocket 뿐이다. */
export const STREAM_PATHS = {
  events: '/events/stream',
  safety: '/safety/stream',
} as const

/**
 * 요청 타임아웃(ms). 조회와 명령을 가른다 —
 * 명령은 장비 왕복이 끼어 더 길고, 조회는 화면이 먼저 「에러 + 다시 시도」로 떨어지는 게 낫다.
 * 🔴 숫자를 화면·클라이언트 코드에 박지 않는다. 실측이 나오면 여기만 바꾼다.
 */
export const REQUEST_TIMEOUT_MS = {
  query: numberFromEnv(import.meta.env.VITE_API_QUERY_TIMEOUT_MS, 15_000),
  command: numberFromEnv(import.meta.env.VITE_API_COMMAND_TIMEOUT_MS, 30_000),
  prepare: numberFromEnv(import.meta.env.VITE_API_PREPARE_TIMEOUT_MS, 60_000),
} as const

/**
 * access 토큰을 미리 갱신하는 시점(TTL 대비 비율).
 * ⚠ **폴링이 아니다** — 토큰 수명 타이머이지 서버 상태 조회가 아니다(DP-01 무관).
 */
export const TOKEN_REFRESH_RATIO = 0.8

/**
 * 구독 이벤트 → 캐시 무효화를 모으는 창(ms).
 * ⚠ **폴링이 아니다** — 이벤트가 없으면 아무 일도 일어나지 않는다. 폭주 방지용 묶음 창이다.
 */
export const STREAM_COALESCE_MS: number = numberFromEnv(
  import.meta.env.VITE_STREAM_COALESCE_MS,
  100,
)

/**
 * 구독 이벤트로 같은 캐시 키를 다시 무효화하기까지의 최소 간격(ms) — 화면 하나가 같은 목록을 이보다 자주 다시 받지 않는다.
 * ⚠ **폴링이 아니다** — 이벤트가 없으면 0건. 첫 이벤트는 `STREAM_COALESCE_MS` 안에 곧바로 흘리고, 잇따른 이벤트만 이 간격으로 모은다.
 * 🔴 2026-10-14 dev2 — 1Hz 모듈 상태 × 4대가 `/modules` 를 초당 여러 번 긁어 서버가 시간 초과를 냈다(`bridge.ts` `minIntervalMs`).
 */
export const STREAM_MIN_REFETCH_MS: number = numberFromEnv(
  import.meta.env.VITE_STREAM_MIN_REFETCH_MS,
  1000,
)

/**
 * 제어락 리스 갱신(heartbeat) 간격을 리스 수명에서 뽑을 때 쓰는 비율.
 *
 * 🔴 **이 값으로 주기를 «정하지» 않는다 — 리스가 정한다.** 간격 = `(expires_at − acquired_at) × 비율`
 *    이라, 서버가 TTL 설정(`CONTROL_LOCK_TTL_SEC`)을 바꾸면 프론트가 따라 움직인다.
 *    TTL 숫자를 프론트에 박으면 서버와 갈려서 **조용히 만료**한다.
 * ⚠ **폴링이 아니다**(DP-01 무관) — 서버 상태를 긁는 것이 아니라 **내가 쥔 리스의 수명을 미는 명령**이다.
 *    락이 없으면 한 번도 돌지 않는다.
 * 근거: 정본 `IFC-COM-04` 「heartbeat 10초 · TTL 30초」 = 1/3 이고, 백엔드 기본값
 * (`CONTROL_LOCK_HEARTBEAT_SEC=10` · `CONTROL_LOCK_TTL_SEC=30`)도 같은 비율이다.
 * 1/3 이면 **연속 2회를 놓쳐도** 아직 만료 전이라 한 번의 실패로 락을 잃지 않는다.
 */
export const CONTROL_LOCK_HEARTBEAT_RATIO: number = ratioFromEnv(
  import.meta.env.VITE_CONTROL_LOCK_HEARTBEAT_RATIO,
  1 / 3,
)

/**
 * 상시 작업 줄 이벤트 칸(`StripEvents`)이 보이는 **최근 행 수**(eric 2026-10-06 「일반적으로 쓰는 방식으로」 — 실시간 로그는
 * 쪽 넘김 없이 최근 것만 위에서부터). 더 오래된 기록 · 내려받기는 작업 상세의 이벤트 탭이 본다.
 */
export const STRIP_EVENTS_TAIL = 50

/**
 * 목록 표 한 쪽의 행 수(eric 2026-09-30 「작업이나 작업 이력 리스트에서.. 리스트로 만들어줘」).
 * 서버 목록 경로의 `limit` 으로 나간다(1~500). 쪽마다 서버에서 받는다 — 전부 받아 자르지 않는다.
 * 지금은 작업 · 이력 두 표가 쓴다. 다른 목록도 쪽 나눔을 붙이면 이 값을 같이 쓴다.
 */
export const LIST_PAGE_SIZE = 20

/**
 * 작업별 이벤트 로그 내려받기(eric 2026-10-02) — `GET /history?batch_id=` 를 끝까지 읽을 때 한 번에 받는 행 수.
 * 서버 상한이 500 이다(`app/views/history.py` `limit le=500`). 넘기면 422 다.
 */
export const EVENT_LOG_EXPORT_PAGE_SIZE = 500

/**
 * 같은 내려받기의 **쪽 수 안전장치** — 이만큼 읽고도 끝이 안 나면 멈추고 실패로 알린다(잘린 파일을 내지 않는다).
 * 500 × 200 = 10만 행. 실측 한 작업은 수백~수천 행이다(2026-10-02).
 */
export const EVENT_LOG_EXPORT_MAX_PAGES = 200

/** 갱신 간격 하한(ms). 리스가 아주 짧게 설정돼도 요청을 몰아치지 않는다 */
export const CONTROL_LOCK_HEARTBEAT_MIN_MS = 2_000

/**
 * 리스 수명을 계산할 수 없을 때의 폴백(ms).
 * ⚠ **지어낸 값이 아니다** — 정본 `IFC-COM-04` 「TTL 30초(D-1)」이자 백엔드 기본값이다.
 *   응답에 `acquired_at`·`expires_at` 이 없을 때만 쓰인다(계약상 둘 다 선택 필드다).
 */
export const CONTROL_LOCK_TTL_FALLBACK_MS = 30_000

/**
 * 클라이언트 식별 문자열의 **환경 덮어쓰기 값**. 로그인에 실제로 실리는 것은 `clientIdentity()`(`device.ts`)다 —
 * 덮어쓰기가 없으면 브라우저별 표지(`web/chrome-linux/<표지>`)가 나가 「내 계정」이 기기별로 묶는다(2026-09-22).
 */
export const CLIENT_ID: string = import.meta.env.VITE_CLIENT_ID?.trim() || 'web/browser'

function numberFromEnv(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/** 0 < 값 < 1 만 받는다 — 비율이 1 이상이면 리스가 만료된 «뒤에» 갱신하게 된다 */
function ratioFromEnv(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 && parsed < 1 ? parsed : fallback
}

/** 상대 base 면 현재 오리진 기준으로 ws:// · wss:// 를 만든다. */
export function streamUrl(path: string): string {
  if (/^https?:\/\//i.test(API_BASE_URL)) {
    return API_BASE_URL.replace(/^http/i, 'ws') + path
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}${API_BASE_URL}${path}`
}
